/**
 * Blog data access.
 *
 * Posts are static JSON files generated at build time by scripts/build-content.mjs
 * from content/posts/*.json and served by nginx (or the CRA dev server) under
 * /api/<lang>/. The index is fetched once per language and every list/filter
 * operation happens client-side - at a few hundred posts this is trivially cheap.
 */
import i18n, { baseLang } from '../i18n';

const currentLang = () => baseLang(i18n.language);
const indexPromises = {};

const loadIndex = (lang = currentLang()) => {
  if (!indexPromises[lang]) {
    indexPromises[lang] = fetch(`/api/${lang}/posts.json`)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch posts index (${res.status})`);
        return res.json();
      })
      .then((data) => (Array.isArray(data?.posts) ? data.posts : []))
      .catch((error) => {
        console.error('Error loading posts index:', error);
        delete indexPromises[lang]; // allow a retry on the next call
        return [];
      });
  }
  return indexPromises[lang];
};

const byNewest = (a, b) => new Date(b.publishedAt || 0) - new Date(a.publishedAt || 0);
const eqi = (a, b) => typeof a === 'string' && typeof b === 'string' && a.toLowerCase() === b.toLowerCase();

/**
 * Get all blog posts with optional filtering, sorting, and pagination
 * @param {Object} options - Query options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Posts per page (default: 9)
 * @param {string} options.category - Filter by category
 * @param {string} options.tag - Filter by tag
 * @param {string} options.query - Free-text search over title, excerpt, category and tags
 * @returns {Promise<Object>} PostsResponse with posts array and pagination info
 */
export const getPosts = async (options = {}) => {
  const page = Math.max(1, Math.floor(Number(options.page) || 1));
  const limit = Math.max(1, Math.floor(Number(options.limit) || 9));

  try {
    let posts = (await loadIndex()).filter((p) => p && p.published !== false);

    if (options.category) posts = posts.filter((p) => eqi(p.category, options.category));
    if (options.tag) posts = posts.filter((p) => Array.isArray(p.tags) && p.tags.some((t) => eqi(t, options.tag)));
    // search before paginating, so a match on a later page is found too
    const query = String(options.query || '').trim().toLowerCase();
    if (query) {
      const has = (s) => typeof s === 'string' && s.toLowerCase().includes(query);
      posts = posts.filter(
        (p) =>
          has(p.title) || has(p.excerpt) || has(p.category) ||
          has(i18n.t(`stories.categories.${p.category}.name`, { defaultValue: '' })) ||
          (Array.isArray(p.tags) && p.tags.some(has))
      );
    }

    posts.sort(byNewest);

    const total = posts.length;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const start = (page - 1) * limit;

    return { posts: posts.slice(start, start + limit), total, page, limit, totalPages };
  } catch (error) {
    console.error('Error in getPosts:', error);
    return { posts: [], total: 0, page, limit, totalPages: 0 };
  }
};

/**
 * Get a single blog post by slug
 * @param {string} slug - Post slug
 * @returns {Promise<Object|null>} BlogPost object or null if not found
 */
export const getPostBySlug = async (slug) => {
  try {
    if (!slug || typeof slug !== 'string' || !/^[a-z0-9-]+$/i.test(slug)) return null;

    const response = await fetch(`/api/${currentLang()}/posts/${encodeURIComponent(slug)}.json`);
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(`Failed to fetch post (${response.status})`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error in getPostBySlug:', error);
    return null;
  }
};

/**
 * Get all categories from published posts
 * @returns {Promise<string[]>} Array of unique category names
 */
export const getCategories = async () => {
  try {
    const posts = await loadIndex();
    return [...new Set(posts.map((p) => p.category).filter(Boolean))];
  } catch (error) {
    console.error('Error in getCategories:', error);
    return [];
  }
};

/**
 * Get all tags from published posts
 * @returns {Promise<string[]>} Array of unique tag names
 */
export const getTags = async () => {
  try {
    const posts = await loadIndex();
    return [...new Set(posts.flatMap((p) => p.tags || []).filter(Boolean))];
  } catch (error) {
    console.error('Error in getTags:', error);
    return [];
  }
};

/**
 * Get related posts based on tags
 * @param {string} currentSlug - Current post slug to exclude
 * @param {string[]} tags - Tags to match
 * @param {number} limit - Maximum number of related posts (default: 3)
 * @returns {Promise<Array>} Array of related blog posts
 */
export const getRelatedPosts = async (currentSlug, tags = [], limit = 3) => {
  try {
    if (!currentSlug || !Array.isArray(tags) || tags.length === 0) return [];

    const posts = await loadIndex();
    return posts
      .filter((p) => p && p.slug !== currentSlug && Array.isArray(p.tags))
      .map((p) => ({ post: p, score: p.tags.filter((t) => tags.some((q) => eqi(q, t))).length }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score || byNewest(a.post, b.post))
      .slice(0, limit)
      .map((x) => x.post);
  } catch (error) {
    console.error('Error in getRelatedPosts:', error);
    return [];
  }
};
